import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { Coffee, ArrowLeft, TrendingUp, Clock, Users, DollarSign, CheckCircle } from 'lucide-react';

const FoodBeverageCaseStudy = () => {
  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <Header />
      
      <article className="pt-40 pb-24 px-6">
        <div className="max-w-6xl mx-auto">
          <Link to="/industries" className="inline-flex items-center gap-2 text-[#2563EB] hover:text-[#3B82F6] mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Industries
          </Link>
          
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center">
                <Coffee className="w-8 h-8 text-white" />
              </div>
              <div>
                <div className="text-sm text-[#2563EB] font-semibold uppercase tracking-wide">
                  Food & Beverage
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-[#F8FAFC]">
                  Urban Harvest Kitchen
                </h1>
              </div>
            </div>
            <p className="text-xl text-[#94A3B8]">
              How a farm-to-table restaurant chain increased average ticket size by 24% through menu optimization
            </p>
          </div>

          <div className="relative rounded-2xl overflow-hidden mb-12 border border-[#2563EB]/20">
            <img
              src="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1200"
              alt="Urban Harvest Kitchen Restaurant"
              className="w-full h-[400px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1C]/60 to-transparent" />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-6">
              <Users className="w-8 h-8 text-[#2563EB] mb-3" />
              <div className="text-2xl font-bold text-[#F8FAFC] mb-1">8</div>
              <div className="text-[#94A3B8]">Restaurant Locations</div>
            </div>
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-6">
              <TrendingUp className="w-8 h-8 text-green-400 mb-3" />
              <div className="text-2xl font-bold text-[#F8FAFC] mb-1">85</div>
              <div className="text-[#94A3B8]">Menu Items</div>
            </div>
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-6">
              <Clock className="w-8 h-8 text-amber-400 mb-3" />
              <div className="text-2xl font-bold text-[#F8FAFC] mb-1">6 years</div>
              <div className="text-[#94A3B8]">In Business</div>
            </div>
          </div>

          <div className="prose prose-invert max-w-none">
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-2xl p-8 mb-12">
              <h2 className="text-2xl font-bold text-[#F8FAFC] mb-4">The Challenge</h2>
              <p className="text-[#94A3B8] text-lg leading-relaxed mb-4">
                Urban Harvest Kitchen built its reputation on farm-to-table dining with locally sourced ingredients. But that commitment came with a pricing nightmare: ingredient costs fluctuated weekly based on seasonal availability and supplier pricing.
              </p>
              <p className="text-[#94A3B8] text-lg leading-relaxed mb-4">
                The management team was updating menu prices quarterly at best, meaning they were often absorbing cost increases for months. Their signature grass-fed burger—the most popular item—hadn't been repriced in 18 months despite beef costs rising 23%.
              </p>
              <p className="text-[#94A3B8] text-lg leading-relaxed">
                Meanwhile, they had no visibility into which menu items were actually profitable. Some dishes that "felt" premium were barely breaking even, while simpler items had margins they didn't realize.
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-2xl p-8 mb-12">
              <h2 className="text-2xl font-bold text-[#F8FAFC] mb-4">The Solution</h2>
              <p className="text-[#94A3B8] text-lg leading-relaxed mb-6">
                PriceStrike helped Urban Harvest Kitchen implement a data-driven approach to menu pricing:
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <span className="text-[#F8FAFC] font-semibold">Cost-Based Pricing Alerts:</span>
                    <span className="text-[#94A3B8]"> Real-time tracking of ingredient costs with automatic alerts when margins drop below thresholds.</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <span className="text-[#F8FAFC] font-semibold">Menu Engineering Analysis:</span>
                    <span className="text-[#94A3B8]"> Identified stars (high profit, high popularity), puzzles (high profit, low popularity), and dogs (low profit, low popularity).</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <span className="text-[#F8FAFC] font-semibold">Competitive Positioning:</span>
                    <span className="text-[#94A3B8]"> Analyzed pricing of similar farm-to-table restaurants to ensure competitive positioning on key items.</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <span className="text-[#F8FAFC] font-semibold">Seasonal Price Recommendations:</span>
                    <span className="text-[#94A3B8]"> Automated suggestions for seasonal menu adjustments based on ingredient availability and cost patterns.</span>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-green-500/10 to-[#111827] border border-green-500/30 rounded-2xl p-8 mb-12">
              <h2 className="text-2xl font-bold text-[#F8FAFC] mb-6">The Results</h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="text-center">
                  <div className="text-5xl font-bold text-green-400 mb-2">24%</div>
                  <div className="text-[#94A3B8]">Increase in average ticket size</div>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold text-green-400 mb-2">$4,200</div>
                  <div className="text-[#94A3B8]">Monthly margin recovered</div>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold text-green-400 mb-2">12</div>
                  <div className="text-[#94A3B8]">Underpriced items identified</div>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold text-green-400 mb-2">Weekly</div>
                  <div className="text-[#94A3B8]">Price optimization reviews</div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#2563EB]/10 to-[#111827] border border-[#2563EB]/30 rounded-2xl p-8 mb-12">
              <blockquote className="text-xl text-[#F8FAFC] italic mb-4">
                "We discovered our signature dish was underpriced by 40%. That single change paid for a year of PriceStrike. Now we actually know which items make us money and which ones just look good on the menu."
              </blockquote>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold">SC</span>
                </div>
                <div>
                  <div className="text-[#F8FAFC] font-semibold">Lisa Thompson</div>
                  <div className="text-[#94A3B8]">Owner & Executive Chef, Urban Harvest Kitchen</div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
            <Link to="/coming-soon">
              <button className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-8 py-4 rounded-lg font-semibold transition-colors">
                Start Your Free Trial
              </button>
            </Link>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default FoodBeverageCaseStudy;
